import React, { useEffect, useState } from 'react';
import { Meet as MeetModel } from '../../../../models/meet';
import { formatDateLocale } from '../../../../utils/formatDateLocale';
import { Book } from '../Book/Book';
import styles from './Meet.module.scss';
import {
  LinkOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  BookOutlined
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { getBook } from '../../../../service/book';
import { useUserStore } from '../../../../store/useUser';
import { getAttendanceByUserAndMeet } from '../../../../service/attendance';
import Icon from '../../../../components/Icon/Icon';
import { button_color, main_color } from '../../../../styles/_variables';
import { Button, Checkbox, Rate, Switch } from 'antd';
import { getClubBookReview, getClubBookReviewByMeet, registerClubBookReview } from '../../../../service/clubBookReview';
import { addBookToUserBookcase } from '../../../../service/bookcase';
import { createErrorNotification, createSuccessNotification } from '../../../../service/notification';
import { isNumberObject } from 'util/types';

interface Props{
  meet: MeetModel;
}

const Content = ({ Icon, label, content }) => {
  return (
    <>
      <br />
      <h1>
        <strong>
          {Icon} {label}
        </strong>
        {content || ''}
      </h1>
    </>
  )
}

const Meet = ({ meet }: Props) => {
  const userId = useUserStore(state => state.id);
  const profile = useUserStore(state => state.profile);
  const [starsRating, setStarsRating] = useState(0);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [generalStarsRating, setGeneralStarsRating] = useState('0.0');
  const [bookRatingAvalaible, setBookRatingAvalaible] = useState(false);

  const { data: book } = useQuery(
    ['books', meet.bookId],
    () => getBook(meet.bookId),
    { refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false }
  );

  const { data: attendance } = useQuery(
    ['attendance', meet.id, 'user', userId],
    () => {
      if(profile === "Clubista") return getAttendanceByUserAndMeet(userId, meet.id, meet.clubId);
      return null;
    },
    { refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false }
  );

  const { data: clubBookReview } = useQuery(
    ['clubBookReview', meet.id, 'user', userId],
    () => getClubBookReview(meet.clubId, meet.id, userId),
    { refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false }
  );

  const { data: clubBookReviewSummary, refetch } = useQuery(
    ['clubBookReviewSummary', meet.id],
    () => getClubBookReviewByMeet(meet.clubId, meet.id),
    { refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false }
  );

  useEffect(() => {
    if(clubBookReview){
      setStarsRating(clubBookReview.starsRating);
      setIsReadOnly(true);
      setBookRatingAvalaible(true);
    }
  },[clubBookReview]);

  useEffect(() => {
    if(clubBookReviewSummary){
      if (typeof clubBookReviewSummary.averageRating === 'number') {
        setGeneralStarsRating(clubBookReviewSummary?.averageRating.toFixed(1));
      }
    }
  },[clubBookReviewSummary]);

  const createReview = async (rating: number) => {
    if (!isReadOnly) {
      setStarsRating(rating);
      setIsReadOnly(true);
      await registerClubBookReview(meet.clubId, meet.id, userId, rating, 1);
      createSuccessNotification("Livro avaliado com sucesso!");

      const { error, success } = await addBookToUserBookcase(userId, meet.bookId, {state: 'read', score: null});
      if(error) createErrorNotification("Não foi possível adicionar a estante!");
      else createSuccessNotification("Livro adicionado a sua estante de usuário!");
    }
    refetch();
  }

  return (
    <div className={styles.item}>
      <div className={styles.meetContainer}>
        <div className={styles.meetBook}>
          <Book
            key={meet.bookId} 
            book={book}/>
        </div>
        <div className={styles.meetInfo}>
          <div className={styles.date}>
            <h1><strong>{formatDateLocale(meet.dateMeet)}</strong></h1>
          </div>
          {book?.name && 
            <Content 
              Icon={<BookOutlined />} 
              content={book.name}
              label={'Livro: '}
              />
          }

          {meet?.link && 
            <Content 
              Icon={<LinkOutlined />} 
              content={meet.link}
              label={'Link: '}
              />
          }
          {meet?.location && 
            <Content 
              Icon={<EnvironmentOutlined />} 
              content={meet.location}
              label={'Local: '}
              />
          }
          {meet?.notes && 
            <Content 
              Icon={<FileTextOutlined />} 
              content={meet.notes}
              label={'Notas: '}
              />
          }
        </div>
      </div>
      <div className={styles.detailsField}>
        <div className={styles.rating}>
          <div className={styles.boxRating} title="Avaliação Geral">
            {generalStarsRating}
          </div>
          <div className={styles.starsReader}>
            {!bookRatingAvalaible
              ? <Button className={styles.button} onClick={() => setBookRatingAvalaible(true)}>
                  Marcar como concluído
                </Button>
              : <Rate 
                  className={styles.customRate}
                  count={5}
                  tooltips={['Péssimo', 'Ruim', 'Normal', 'Bom', 'Excelente']}
                  onChange={(value) => createReview(value)}
                  value={starsRating}
                  disabled={isReadOnly}
                />
            }
            
            <span>
              {clubBookReviewSummary?.totalReaders} leitores concluiram
            </span>
          </div>
        </div>
        {attendance !== null &&
          (attendance?.wasInAttendance === 1
            ? <div className={styles.attendance}><span className={styles.checked}>Presente</span><Icon icon="checkbox-checked" color={button_color}/></div>
            : attendance?.wasInAttendance === 0 
              ? <div className={styles.attendance}><span className={styles.notChecked}>Ausente</span><Icon icon="checkbox-x" color={main_color}/></div>
              : <div className={styles.attendance}>Não há registro de presença / ausência</div>
          )
        }
      </div>
    </div>
  )
}

export default Meet;